import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import DefaultApiLimit from '../../../../../config/defaultLimit';
import DefaultApiParameters from '../../../../../enum/DefaultApiParameters';
import {
  ChargingScheduleProperties,
  ChargingScheduleRequest,
} from '../../../../../interface/charging-schedule.interface';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import {
  fetchChargingScheduleFilters,
  fetchCharingSchedules,
  refreshChargingSchedules,
  setSortData,
  updateChargingScheduleFilters,
} from '../../../../../redux/slices/ChargingScheduleSlice';
import CustomTable from '../../../../../shared-components/custom-table/CustomTable';
import { GetParametersFromCommonFilters } from '../../../../../shared-functions/CommonFilters';
import { capitalizeFirstLetter } from '../../../../../shared-functions/StringFunctions';

/**
 * Component to disable charging schedule based on charging point
 * @returns {any} The result
 */
function ChargingSchedules() {
  const { t } = useTranslation('devices');
  const dispatch = useAppDispatch();
  const {
    schedules,
    limit,
    page,
    loading,
    totalPages,
    totalResults,
    sortField,
    sortBy,
    filterListLoading,
    filters,
  } = useAppSelector((s) => s.chargingSchedule);
  const { selectedChargingPoint } = useAppSelector((s) => s.device);

  const column = [
    {
      label: t('bookingFrom'),
      accessorKey: 'bookingFrom' as keyof ChargingScheduleProperties,
      sortAllow: true,
    },
    {
      label: t('bookingTo'),
      accessorKey: 'bookingTo' as keyof ChargingScheduleProperties,
      sortAllow: true,
    },
    {
      label: t('stationName'),
      accessorKey: 'chargingStation' as keyof ChargingScheduleProperties,
      sortAllow: true,
    },
    {
      label: t('vehicleDetails'),
      accessorKey: 'vehicleModel' as keyof ChargingScheduleProperties,
      sortAllow: true,
    },
    {
      label: t('userId'),
      accessorKey: 'userCustomId' as keyof ChargingScheduleProperties,
      sortAllow: true,
    },

    {
      label: t('status'),
      accessorKey: 'status' as keyof ChargingScheduleProperties,
      sortAllow: true,
      /**
       * Payment status view in table
       * @param {any} payoutsStatus - The payoutsStatus
       * @returns {any} The result
       */
      customData: (payoutsStatus: ChargingScheduleProperties) => (
        <div className="status-under-charging-schedule-table">
          <p
            className={`${payoutsStatus.status === 'completed' ? 'success-text' : 'pending-text'}`}>
            {capitalizeFirstLetter(payoutsStatus.status)}
          </p>
        </div>
      ),
    },
  ];

  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    dispatch(
      fetchCharingSchedules({
        ...queryParameters,
        sortBy: sortData.sortBy,
        sortField: sortData.sortField,
        page: DefaultApiLimit.page,
        limit,
        chargingPointSn: selectedChargingPoint?.chargingPointSn || '',
      }),
    );
  };

  /**
   * Table pagination function
   * @param {any} value - The value
   */
  const pageNateLimitChange = (value: ChargingScheduleRequest) => {
    dispatch(
      fetchCharingSchedules({
        ...value,
        sortField,
        sortBy,
        chargingPointSn: selectedChargingPoint?.chargingPointSn || '',
      }),
    );
  };

  /**
   * handle auto refresh
   */
  const handleAutoRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);

    dispatch(
      refreshChargingSchedules({
        ...queryParameters,
        page,
        limit,
        sortBy,
        sortField,
        chargingPointSn: selectedChargingPoint?.chargingPointSn || '',
      }),
    );
  };

  useEffect(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);

    dispatch(
      fetchCharingSchedules({
        page: DefaultApiParameters.page,
        limit: DefaultApiParameters.limit,
        chargingPointSn: selectedChargingPoint?.chargingPointSn || '',
        ...queryParameters,
      }),
    );
  }, [filters, selectedChargingPoint]);

  return (
    <div className="device-charging-schedules-wrap">
      <div className="device-charging-schedules-table">
        <CustomTable
          header={column}
          data={schedules}
          serialNo
          filter={{}}
          filterClearFunction={() => {}}
          filterFunction={() => {}}
          actionButton={false}
          limit={limit}
          page={page}
          loading={loading}
          totalPages={totalPages}
          totalResults={totalResults}
          sortData={{ sortBy, sortField }}
          handleSortChange={handleSortFunction}
          pageLimitChange={(value: any) => pageNateLimitChange(value)}
          sizeChangeButtonRequired
          requiredRefreshToggle
          refreshFunction={handleAutoRefresh}
          filters={filters}
          filtersToDispatch={updateChargingScheduleFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchChargingScheduleFilters}
          version="v2"
          height={400}
        />
      </div>
    </div>
  );
}

export default ChargingSchedules;
